import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { fmtZMW, fmtDate, exportCSV, can } from "@/lib/helpers";
import { useAuth } from "@/contexts/AuthContext";
import {
  PageHeader, BtnPrimary, BtnSecondary, BtnDanger, BtnGhost,
  Modal, Field, Input, Select, Textarea, Empty, StatusBadge, PriorityBadge,
} from "@/components/primitives";
import LineItemsEditor from "@/components/LineItemsEditor";
import { toast } from "sonner";
import { Plus, DownloadSimple, Eye, Check, X, ClipboardText } from "@phosphor-icons/react";
import ProjectSelect from "@/components/ProjectSelect";
import SmartInput from "@/components/SmartInput";
import MicDictate from "@/components/MicDictate";

const TABS = [
  { id: "work_order", label: "Work Orders", short: "WO" },
  { id: "parts_request", label: "Parts Requests", short: "PR" },
  { id: "justification", label: "Justifications", short: "JF" },
];

export default function RequestsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("work_order");
  const [rows, setRows] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(null);
  const [skus, setSkus] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [machines, setMachines] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", priority: "medium",
    asset_type: "", asset_ref: "", cost_center: "", estimated_cost: 0,
  });
  const [items, setItems] = useState([]);
  const [decisionNote, setDecisionNote] = useState("");
  const canApprove = can(user.role, "manager");
  const canCreate = can(user.role, "staff");

  async function load() {
    try { const { data } = await api.get(`/requests?type=${tab}`); setRows(data); }
    catch (e) { toast.error(formatError(e)); }
  }
  useEffect(() => { load(); }, [tab]);
  useEffect(() => {
    (async () => {
      try {
        const [i, t, m] = await Promise.all([
          api.get("/inventory"), api.get("/trucks"), api.get("/machines"),
        ]);
        setSkus(i.data); setTrucks(t.data); setMachines(m.data);
      } catch (e) { if (process.env.NODE_ENV !== "production") console.debug("non-fatal:", e); }
      try {
        const { data } = await api.get("/lookups/cost_center");
        setCostCenters(data || []);
      } catch (_e) { /* non-fatal */ }
    })();
  }, []);

  function openCreate() {
    setForm({ title: "", description: "", priority: "medium", asset_type: "", asset_ref: "", project_code: "", cost_center: "", estimated_cost: 0 });
    setItems([]); setCreateOpen(true);
  }

  async function submit(e) {
    e.preventDefault();
    try {
      await api.post("/requests", {
        type: tab,
        title: form.title,
        description: form.description,
        priority: form.priority,
        asset_ref: form.asset_ref || null,
        asset_type: form.asset_type || null,
        project_code: form.project_code || null,
        cost_center: form.cost_center || null,
        estimated_cost: Number(form.estimated_cost || 0),
        items: tab === "parts_request"
          ? items.map((i) => ({
              description: i.description, sku: i.sku || null,
              quantity: Number(i.quantity), unit_price: Number(i.unit_price),
            }))
          : [],
      });
      toast.success("Request submitted");
      setCreateOpen(false); load();
    } catch (e) { toast.error(formatError(e)); }
  }

  async function decide(action) {
    try {
      await api.post(`/requests/${viewOpen.id}/${action}`, { note: decisionNote });
      toast.success(`Request ${action}ed`);
      setViewOpen(null); setDecisionNote(""); load();
    } catch (e) { toast.error(formatError(e)); }
  }
  async function complete() {
    try {
      await api.post(`/requests/${viewOpen.id}/complete`);
      toast.success("Marked completed");
      setViewOpen(null); load();
    } catch (e) { toast.error(formatError(e)); }
  }

  const assetOptions = useMemo(() => {
    if (form.asset_type === "truck") return trucks.map((t) => ({ id: t.id, label: `${t.plate} — ${t.make} ${t.model}` }));
    if (form.asset_type === "machine") return machines.map((m) => ({ id: m.id, label: `${m.asset_tag} — ${m.name}` }));
    return [];
  }, [form.asset_type, trucks, machines]);

  return (
    <div data-testid="requests-page">
      <PageHeader
        overline="Module / Requests"
        title="Requests Hub"
        subtitle="Work orders, parts requests and justifications — all approved in one place."
        actions={
          <>
            <BtnSecondary onClick={() => exportCSV(rows, `${tab}-${Date.now()}.csv`)}>
              <DownloadSimple size={14} weight="bold" className="inline mr-1.5" /> CSV
            </BtnSecondary>
            {canCreate && (
              <BtnPrimary onClick={openCreate} data-testid="req-create">
                <Plus size={14} weight="bold" className="inline mr-1.5" /> New {TABS.find((t) => t.id === tab).short}
              </BtnPrimary>
            )}
          </>
        }
      />

      <div className="flex border-b border-[#e4e4e7] mb-4">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`req-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? "border-[#002FA7] text-[#09090b]"
                : "border-transparent text-[#52525b] hover:text-[#09090b]"
            }`}
          >{t.label}</button>
        ))}
      </div>

      <div className="phizo-card">
        {rows.length === 0 ? <div className="p-5"><Empty label="No records in this tab." /></div> : (
          <div className="overflow-x-auto">
            <table className="phizo-table">
              <thead>
                <tr>
                  <th>Number</th><th>Title</th><th>Priority</th>
                  <th>Requested By</th><th>Created</th>
                  <th className="text-right">Est. Cost</th>
                  <th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    data-testid={`req-row-${r.number}`}
                    onClick={() => nav(`/requests/${r.id}`)}
                    className="cursor-pointer hover:bg-[#f4f4f5]"
                  >
                    <td className="mono font-semibold">{r.number}</td>
                    <td className="font-medium">{r.title}</td>
                    <td><PriorityBadge priority={r.priority} /></td>
                    <td className="text-sm text-[#52525b]">{r.requested_by_name}</td>
                    <td className="text-sm text-[#52525b]">{fmtDate(r.created_at)}</td>
                    <td className="text-right mono">{fmtZMW(r.estimated_cost)}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td className="text-right" onClick={(e) => e.stopPropagation()}>
                      <BtnGhost onClick={() => { setViewOpen(r); setDecisionNote(""); }}
                        data-testid={`req-view-${r.number}`}>
                        <Eye size={12} weight="bold" /> View
                      </BtnGhost>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)}
        title={`New ${TABS.find((t) => t.id === tab).label}`} wide testId="req-create-modal">
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Title">
              <Input required data-testid="r-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </Field>
            <Field label="Priority">
              <Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                <option value="low">low</option>
                <option value="medium">medium</option>
                <option value="high">high</option>
                <option value="urgent">urgent</option>
              </Select>
            </Field>
          </div>
          <Field label="Description / Justification">
            <Textarea required data-testid="r-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Asset Type (opt)">
              <Select value={form.asset_type} onChange={(e) => setForm({ ...form, asset_type: e.target.value, asset_ref: "" })}>
                <option value="">—</option>
                <option value="truck">Truck</option>
                <option value="machine">Machine</option>
              </Select>
            </Field>
            <Field label="Asset">
              <Select value={form.asset_ref} onChange={(e) => setForm({ ...form, asset_ref: e.target.value })}
                disabled={!form.asset_type}>
                <option value="">—</option>
                {assetOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </Select>
            </Field>
            <Field label="Estimated Cost (ZMW)">
              <Input type="number" step="0.01" value={form.estimated_cost}
                onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })} />
            </Field>
          </div>
          <Field label="Project / Cost-centre (optional)">
            <ProjectSelect value={form.project_code}
              onChange={(v) => setForm({ ...form, project_code: v })}
              testId="r-project" />
          </Field>
          <Field label="Cost Center" hint="From master list or type a new one">
            <SmartInput
              testId="r-cost-center"
              value={form.cost_center || ""}
              onChange={(v) => setForm({ ...form, cost_center: v })}
              options={costCenters.map((c) => ({ value: c.name, hint: c.code || "" }))}
              placeholder="e.g. Crushing"
            />
          </Field>

          {tab === "parts_request" && (
            <LineItemsEditor items={items} setItems={setItems} skuOptions={skus} />
          )}

          <div className="flex justify-end gap-2 pt-3 border-t border-[#e4e4e7]">
            <BtnSecondary type="button" onClick={() => setCreateOpen(false)}>Cancel</BtnSecondary>
            <BtnPrimary type="submit" data-testid="r-submit">Submit Request</BtnPrimary>
          </div>
        </form>
      </Modal>

      {/* View */}
      <Modal open={!!viewOpen} onClose={() => setViewOpen(null)}
        title={viewOpen ? `${viewOpen.number} — ${viewOpen.title}` : ""} wide testId="req-view-modal">
        {viewOpen && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div><div className="phizo-label">Type</div><div>{viewOpen.type.replace("_", " ")}</div></div>
              <div><div className="phizo-label">Priority</div><PriorityBadge priority={viewOpen.priority} /></div>
              <div><div className="phizo-label">Status</div><StatusBadge status={viewOpen.status} /></div>
              <div><div className="phizo-label">Requested By</div><div>{viewOpen.requested_by_name}</div></div>
              <div><div className="phizo-label">Created</div><div>{fmtDate(viewOpen.created_at)}</div></div>
              <div><div className="phizo-label">Est. Cost</div><div className="mono">{fmtZMW(viewOpen.estimated_cost)}</div></div>
            </div>
            <div className="phizo-surface p-3 text-sm">
              <div className="phizo-label mb-1">Description</div>
              <div className="whitespace-pre-line">{viewOpen.description}</div>
            </div>

            {viewOpen.items?.length > 0 && (
              <div className="border border-[#e4e4e7]">
                <table className="w-full text-sm">
                  <thead className="bg-[#f4f4f5]"><tr>
                    <th className="text-left p-2 phizo-label">Part</th>
                    <th className="text-left p-2 phizo-label">SKU</th>
                    <th className="text-right p-2 phizo-label">Qty</th>
                    <th className="text-right p-2 phizo-label">Est. Unit</th>
                  </tr></thead>
                  <tbody>
                    {viewOpen.items.map((it, idx) => (
                      <tr key={idx} className="border-t border-[#e4e4e7]">
                        <td className="p-2">{it.description}</td>
                        <td className="p-2 mono text-xs">{it.sku || "—"}</td>
                        <td className="p-2 text-right mono">{it.quantity}</td>
                        <td className="p-2 text-right mono">{fmtZMW(it.unit_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {viewOpen.approver_name && (
              <div className="phizo-surface p-3 text-sm">
                <div className="phizo-label mb-1">Decision</div>
                <div>{viewOpen.status.toUpperCase()} by {viewOpen.approver_name}</div>
                {viewOpen.decision_note && <div className="text-[#52525b] mt-1">"{viewOpen.decision_note}"</div>}
              </div>
            )}

            {viewOpen.status === "pending" && canApprove && (
              <div className="space-y-2">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="phizo-label">Decision Note (optional)</label>
                    <MicDictate
                      context="incident report; approval decision note; mining operations"
                      onTranscript={(text) => setDecisionNote((n) => (n ? n + " " : "") + text)}
                      testId="r-note-mic"
                    />
                  </div>
                  <Textarea data-testid="r-note" value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} />
                </div>
                <div className="flex justify-end gap-2">
                  <BtnDanger onClick={() => decide("reject")} data-testid="r-reject">
                    <X size={14} weight="bold" className="inline mr-1" /> Reject
                  </BtnDanger>
                  <BtnPrimary onClick={() => decide("approve")} data-testid="r-approve">
                    <Check size={14} weight="bold" className="inline mr-1" /> Approve
                  </BtnPrimary>
                </div>
              </div>
            )}
            {viewOpen.status === "approved" && canCreate && (
              <div className="flex justify-end">
                <BtnPrimary onClick={complete} data-testid="r-complete">Mark Completed</BtnPrimary>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
